import { useRef, useEffect, useState } from "react";

import { TooltipBase } from "@frontend/common";

import { PublicationListData } from "@/components/business/PublicationListContainer/publicationList.types";
import LabelingPressTitle from "@/components/shared/ui/LabelingPressTitle/LabelingPressTitle";
import { ProcessStatusCode } from "@/types/code.types";
import { ctgListApis } from "@api/ctg/ctgList/ctgList.service";
import { DRAFT_STATUS_CODE } from "@constants/draftStatusCode.constants";
import { useGetCurrentEntryData } from "@hooks/useGetCurrentEntryData";
import { toastFail } from "@utils/toast";

import { useMovePublicationDetailPage } from "./hooks/useMovePublicationDetailPage";
import * as S from "./ListTableShellForTitle.style";

interface Props {
  publicationData: PublicationListData;
  openCheckStatusModal: () => void;
  handleTargetCtgId: () => void;
  handleCurrentStatus: (status: ProcessStatusCode | "") => void;
}

const ListTableShellForTitle = ({
  publicationData,
  openCheckStatusModal,
  handleTargetCtgId,
  handleCurrentStatus,
}: Props) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const titleRef = useRef<HTMLDivElement>(null);
  const getEntryData = useGetCurrentEntryData(
    publicationData?.publicationId?.toString(),
  );

  useEffect(() => {
    const checkTruncation = () => {
      if (titleRef.current) {
        const { scrollHeight, clientHeight } = titleRef.current;
        setIsTruncated(scrollHeight > clientHeight + 1);
      }
    };

    setTimeout(checkTruncation, 0);
    window.addEventListener("resize", checkTruncation);

    return () => {
      window.removeEventListener("resize", checkTruncation);
    };
  }, [publicationData?.title]);

  const { movePublicationDetailPage } = useMovePublicationDetailPage({
    draftArticleId: publicationData?.publicationId?.toString(),
  });

  const saveScrollPositionToSessionStorage = () => {
    sessionStorage.setItem("scrollPosition", window.scrollY.toString());
  };

  const checkCurrentStatus = async () => {
    try {
      const { data: currentStatus } = await ctgListApis.checkPressStatus(
        publicationData?.publicationId?.toString(),
      );

      // currentStatus가 ai done, draftArticleStatusHighest가 not for ai인 경우 Alert이 표출되지 않도록 설정
      if (
        currentStatus === DRAFT_STATUS_CODE.aiEntryDone &&
        publicationData.draftArticleStatusHighest ===
          DRAFT_STATUS_CODE.notForAiEntry
      ) {
        saveScrollPositionToSessionStorage();
        movePublicationDetailPage();

        return;
      }
      if (currentStatus !== publicationData.draftArticleStatusHighest) {
        handleCurrentStatus(currentStatus as ProcessStatusCode | "");
        handleTargetCtgId();
        openCheckStatusModal();
      } else {
        saveScrollPositionToSessionStorage();
        movePublicationDetailPage();
      }
    } catch (error) {
      toastFail("Could not check the current status of the article.");
    }
  };

  const title = publicationData?.title || "-";

  const clickToMovePressDetailPage = async () => {
    getEntryData();
    await checkCurrentStatus();
  };

  return (
    <S.PressTitleTableShell onClick={clickToMovePressDetailPage}>
      <S.TitleWrapper>
        <div ref={titleRef}>
          <LabelingPressTitle title={title} />
        </div>
      </S.TitleWrapper>
      {isTruncated && (
        <S.TooltipWrapper className="tooltip-container">
          <TooltipBase className="title-tooltip">
            <LabelingPressTitle title={title} />
          </TooltipBase>
        </S.TooltipWrapper>
      )}
    </S.PressTitleTableShell>
  );
};

export default ListTableShellForTitle;
